import React, { useEffect, useState } from 'react';
import { Box, Button, Popover, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GreenCheck } from '../custom-components/CustomCheckboxes';

export interface FilterOption {
  /** The value stored in the corresponding filter array (a status bucket label, project id, or
   * document type). */
  value: string;
  label: string;
  count: number;
}

interface Props {
  dialogOpen: boolean;
  handleClose: () => void;
  anchor: any;
  statusOptions: FilterOption[];
  overdueOption: FilterOption | null;
  projectOptions: FilterOption[];
  /** Omit the Project section entirely when already viewing a single project's tasks — filtering
   * by project is meaningless when every row already belongs to the one project in view. */
  showProjectFilter: boolean;
  docTypeOptions: FilterOption[];
  statusFilters: string[];
  projectFilters: string[];
  docTypeFilters: string[];
  onApply: (statusFilters: string[], projectFilters: string[], docTypeFilters: string[]) => void;
}

const useStyles = makeStyles({
  section: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 260,
    maxWidth: 320,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
  },
  selectList: {
    maxHeight: 260,
    overflowY: 'auto',
  },
  inline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    paddingTop: 16,
  },
  button: {
    width: 100,
    height: 36,
  },
});

/**
 * Filter dialog for the My Work page (status incl. Overdue, Project, Document Type). Unlike
 * DocumentLogFilterDialog — built around a single selected document type from Redux and used on
 * every document log page — this is a dedicated component so extending it stays scoped to My
 * Tasks with no risk of regressing filtering on other document logs. Every option list is passed
 * in already reduced to what's actually present in the current My Work document set (item 10:
 * dynamic population), so a project/status/type with zero matching rows never appears.
 */
export default function MyTasksFilterDialog({
  dialogOpen,
  handleClose,
  anchor,
  statusOptions,
  overdueOption,
  projectOptions,
  showProjectFilter,
  docTypeOptions,
  statusFilters,
  projectFilters,
  docTypeFilters,
  onApply,
}: Props) {
  const classes = useStyles();

  const [localStatus, setLocalStatus] = useState<string[]>(statusFilters);
  const [localProjects, setLocalProjects] = useState<string[]>(projectFilters);
  const [localDocTypes, setLocalDocTypes] = useState<string[]>(docTypeFilters);

  useEffect(() => {
    setLocalStatus(statusFilters);
    setLocalProjects(projectFilters);
    setLocalDocTypes(docTypeFilters);
  }, [statusFilters, projectFilters, docTypeFilters, dialogOpen]);

  const toggle = (list: string[], setList: (v: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const handleClear = () => {
    setLocalStatus([]);
    setLocalProjects([]);
    setLocalDocTypes([]);
    onApply([], [], []);
    handleClose();
  };

  const handleUpdate = () => {
    onApply(localStatus, localProjects, localDocTypes);
    handleClose();
  };

  // Status options like "Awaiting Submission / Not Started" force a line break right after the
  // "/" instead of wrapping wherever the text happens to run out of room.
  const renderOptionLabel = (label: string, count: number) => {
    if (label.includes(' / ')) {
      const [first, second] = label.split(' / ');
      return (
        <>
          {first} /<br />
          {second} ({count})
        </>
      );
    }
    return `${label} (${count})`;
  };

  const renderOptionList = (
    options: FilterOption[],
    selected: string[],
    setSelected: (v: string[]) => void,
  ) => (
    <div className={classes.selectList}>
      {options.map((option) => (
        <div key={option.value} className={classes.optionRow}>
          <GreenCheck
            style={{ padding: 3 }}
            checked={selected.includes(option.value)}
            onChange={() => toggle(selected, setSelected, option.value)}
          />
          <Typography style={{ display: 'flex', alignItems: 'center', minHeight: 30 }}>
            {renderOptionLabel(option.label, option.count)}
          </Typography>
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      open={dialogOpen}
      onClose={handleUpdate}
      anchorEl={anchor}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box className={classes.section}>
        <Typography variant="body1" style={{ marginBottom: 6 }}>
          Status
        </Typography>
        {renderOptionList(statusOptions, localStatus, setLocalStatus)}
        {overdueOption && (
          <div className={classes.optionRow} style={{ marginTop: 2 }}>
            <GreenCheck
              style={{ padding: 3 }}
              checked={localStatus.includes(overdueOption.value)}
              onChange={() => toggle(localStatus, setLocalStatus, overdueOption.value)}
            />
            <Typography style={{ height: 30, display: 'flex', alignItems: 'center' }}>
              Overdue ({overdueOption.count})
            </Typography>
          </div>
        )}
      </Box>

      {showProjectFilter && (
        <Box className={classes.section}>
          <Typography variant="body1" style={{ marginBottom: 6 }}>
            Project
          </Typography>
          {renderOptionList(projectOptions, localProjects, setLocalProjects)}
        </Box>
      )}

      <Box className={classes.section}>
        <Typography variant="body1" style={{ marginBottom: 6 }}>
          Document Type
        </Typography>
        {renderOptionList(docTypeOptions, localDocTypes, setLocalDocTypes)}
      </Box>

      <Box className={classes.section} style={{ paddingTop: 0 }}>
        <Box className={classes.inline}>
          <Button
            onClick={handleClear}
            variant="outlined"
            className={classes.button}
            style={{ width: '48%' }}
          >
            Clear All
          </Button>
          <Button
            onClick={handleUpdate}
            color="primary"
            variant="contained"
            className={classes.button}
            style={{ width: '48%' }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
